import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';

type UserRole = 'owner' | 'admin' | 'recruiter' | 'viewer';

interface User {
  userId: string;
  email: string;
  name: string;
  role: UserRole;
  status: 'active' | 'disabled';
}

interface UserTableProps {
  users: User[];
  onUserUpdated: () => void;
}

const roleLabels: Record<UserRole, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  recruiter: 'Recrutador',
  viewer: 'Visualizador',
};

const statusLabels = {
  active: 'Ativo',
  disabled: 'Desativado',
};

export function UserTable({ users, onUserUpdated }: UserTableProps) {
  const { user: currentUser } = useAuth();
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canManageUsers =
    currentUser?.role === 'owner' || currentUser?.role === 'admin';

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    setError(null);
    setLoadingUserId(userId);

    try {
      await apiClient.patch(`/users/${userId}/role`, { role: newRole });
      onUserUpdated();
    } catch (err: any) {
      setError(err.message || 'Falha ao alterar função');
    } finally {
      setLoadingUserId(null);
    }
  };

  const handleRemoveUser = async (userId: string, userName: string) => {
    if (
      !confirm(
        `Tem certeza que deseja remover ${userName}? Esta ação não pode ser desfeita.`
      )
    ) {
      return;
    }

    setError(null);
    setLoadingUserId(userId);

    try {
      await apiClient.delete(`/users/${userId}`);
      onUserUpdated();
    } catch (err: any) {
      setError(err.message || 'Falha ao remover usuário');
    } finally {
      setLoadingUserId(null);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
          {error}
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>E-mail</TableHead>
            <TableHead>Função</TableHead>
            <TableHead>Status</TableHead>
            {canManageUsers && <TableHead className="text-right">Ações</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const isCurrentUser = user.userId === currentUser?.userId;
            const isLoading = loadingUserId === user.userId;
            const canModify = canManageUsers && !isCurrentUser;

            return (
              <TableRow key={user.userId}>
                <TableCell className="font-medium">
                  {user.name}
                  {isCurrentUser && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      (você)
                    </span>
                  )}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {canModify && user.status === 'active' ? (
                    <Select
                      value={user.role}
                      onChange={(e) =>
                        handleRoleChange(user.userId, e.target.value as UserRole)
                      }
                      disabled={isLoading}
                      className="w-40"
                    >
                      {currentUser?.role === 'owner' ? (
                        <>
                          <option value="owner">Proprietário</option>
                          <option value="admin">Administrador</option>
                          <option value="recruiter">Recrutador</option>
                          <option value="viewer">Visualizador</option>
                        </>
                      ) : (
                        <>
                          <option value="admin">Administrador</option>
                          <option value="recruiter">Recrutador</option>
                          <option value="viewer">Visualizador</option>
                        </>
                      )}
                    </Select>
                  ) : (
                    <span>{roleLabels[user.role]}</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={user.status === 'active' ? 'default' : 'secondary'}
                  >
                    {statusLabels[user.status]}
                  </Badge>
                </TableCell>
                {canManageUsers && (
                  <TableCell className="text-right">
                    {canModify && user.status === 'active' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveUser(user.userId, user.name)}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Removendo...' : 'Remover'}
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
